import { trigger, transition, style, animate } from '@angular/animations';
import { MatStepper } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, HostListener, DestroyRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { SharedService } from '@shared/services/shared.service';
import { AddressComponent } from 'app/components/reusable/address/address.component';
import { EntityOwnerComponent } from 'app/components/reusable/entity-owner/entity-owner.component';
import { IStatus } from 'app/models/licenses/status';
import { firstValueFrom, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IHospitalLicense } from 'app/models/licenses/hospital/hospital-license';
import { IRepresentativeContact } from 'app/models/licenses/hospital/representative-contact';
import { HospitalLicenseService } from 'app/services/license/hospital/hospital-license.service';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { OnCampusBuildingComponent } from 'app/components/reusable/on-campus-building/on-campus-building.component';
import { SatelliteLocationComponent } from 'app/components/reusable/satellite-location/satellite-location.component';
import { ILicenseRequest } from 'app/models/licenses/license-request';
import { AuthService } from '@shared/auth/auth.service';
import { HospitalLicenseValidator } from '@shared/validators/hospital-license-validator';
import { HospitalChiefNurseValidator } from '@shared/validators/hospital-chief-nurse-validator';
import { hospitalContactNamesValidator, hospitalContactNameLabels, HospitalContactNameErrorStateMatcher } from '@shared/validators/hospital-contact-names-validator';
import { DocumentComponent } from 'app/components/reusable/document/document.component';
import { IDocument } from 'app/models/documents/document';
import { MatSort } from '@angular/material/sort';
import { HospitalSnapshotComponent, snapshotSection } from './hospital-snapshot.component';

export const MM_DD_FORMATS = {
  parse: {
    dateInput: 'MM/DD',
  },
  display: {
    dateInput: 'MM/DD',
    monthYearLabel: 'MMM',
    dateA11yLabel: 'MM/DD',
    monthYearA11yLabel: 'MMM'
  }
};

@Component({
  selector: 'app-hospital',
  templateUrl: './hospital.component.html',
  styleUrls: ['./hospital.component.scss'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: MM_DD_FORMATS }]
})
export class HospitalComponent implements OnInit, OnDestroy {
  @ViewChild('Stepper') stepper!: MatStepper;
  public savingStep = false;
  public validatingLocations = false;
  public submitting = false;

  @HostListener('window:beforeunload', ['$event'])
  warnUnsaved(event: BeforeUnloadEvent): void {
    if ([this.onCampusBuildings?.saver, this.satelliteLocation?.saver,
      this.onCampusBuildings?.serviceLocations?.saver, this.satelliteLocation?.serviceLocations?.saver]
      .some(saver => saver?.pending) || this.savingStep || this.onCampusBuildings?.isCreating ||
      this.satelliteLocation?.isCreating || this.onCampusBuildings?.serviceLocations?.isSaving ||
      this.satelliteLocation?.serviceLocations?.isSaving) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  async saveLocations(): Promise<boolean> {
    if (this.locked) return true;
    if (this.onCampusBuildings && !await this.onCampusBuildings.savePending()) return false;
    if (this.satelliteLocation && !await this.satelliteLocation.savePending()) return false;
    return true;
  }

  async canLeave(): Promise<boolean> {
    if (this.savingStep || this.submitting) return false;
    this.savingStep = true;
    try { return this.license.id > 0 && !this.locked ? await this.saveLicense() : true; }
    finally { this.savingStep = false; }
  }

  async validateLocationDrafts(): Promise<string[]> {
    if (this.classificationForm.get('isSicf')?.value) return [];
    try {
      const buildingErrors = await this.onCampusBuildings?.validateServiceLocations() ?? [];
      const satelliteErrors = await this.satelliteLocation?.validateServiceLocations() ?? [];
      return [...buildingErrors, ...satelliteErrors];
    } catch {
      return ['Could not verify service locations. Please retry before submitting.'];
    }
  }

  @ViewChild(HospitalSnapshotComponent) snapshot!: HospitalSnapshotComponent;

  exportSnapshot(): void {
    const license = { ...this.license, ...this.classificationForm.getRawValue(),
      ...this.licenseForm.getRawValue(), ...this.contactForm.getRawValue(), ...this.servicesForm.getRawValue() };
    // Preserve edits in open related-record editors without saving or submitting them.
    const drafts = [];
    if (this.owners?.managingOwner && this.owners.ownerForm.dirty) {
      drafts.push(snapshotSection('Owner — unsaved edits', this.owners.ownerForm.getRawValue()));
    }
    if (this.showRepresentativeContactForm && this.representativeContactForm.dirty) {
      drafts.push(snapshotSection('Representative contact — unsaved edits', this.representativeContactForm.getRawValue()));
    }
    const building = this.onCampusBuildings;
    const satellite = this.satelliteLocation;
    if (building?.managingBuilding && building.buildingForm.dirty) {
      drafts.push(snapshotSection('On-campus building — unsaved edits', building.buildingForm.getRawValue()));
    }
    if (satellite?.managingSatelliteLocation && satellite.satelliteForm.dirty) {
      drafts.push(snapshotSection('Satellite location — unsaved edits', satellite.satelliteForm.getRawValue()));
    }
    for (const editor of [building?.serviceLocations, satellite?.serviceLocations]) {
      if (editor?.managingLocation && editor.locationForm.dirty) {
        drafts.push(snapshotSection(`${editor.entityType} service — unsaved edits`, editor.locationForm.getRawValue()));
      }
    }
    const addressEditors = [this.addresses, ...(this.owners?.addresses?.toArray() || []),
      ...(building?.addresses?.toArray() || []), ...(satellite?.addresses?.toArray() || [])];
    for (const editor of addressEditors) {
      if (editor?.addingAddress && editor.addressForm.dirty) {
        const address = editor.addressForm.getRawValue();
        drafts.push(snapshotSection(`${editor.entityType} address — unsaved edits`, {
          ...address, types: (address.types || []).map(type => type.type)
        }));
      }
    }
    void this.snapshot.download(license, [
      snapshotSection('Classification', this.classificationForm.getRawValue()),
      snapshotSection('Demographics', this.licenseForm.getRawValue()),
      snapshotSection('Contacts', this.contactForm.getRawValue()),
      snapshotSection('Services', { ...this.servicesForm.getRawValue(), swingBedsCount: license.swingBedsCount }),
      ...drafts
    ]);
  }

  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChild(AddressComponent) addresses!: AddressComponent;
  @ViewChild(EntityOwnerComponent) owners!: EntityOwnerComponent;
  @ViewChild(OnCampusBuildingComponent) onCampusBuildings!: OnCampusBuildingComponent;
  @ViewChild(SatelliteLocationComponent) satelliteLocation!: SatelliteLocationComponent;

  public authService = inject(AuthService);
  public sharedService = inject(SharedService);
  public licenseService = inject(HospitalLicenseService);
  public licenseValidator = inject(HospitalLicenseValidator);
  public chiefNurseValidator = inject(HospitalChiefNurseValidator);
  private readonly destroyRef = inject(DestroyRef);

  public hospitalDocumentTypes: string[] = ['Admin Signature'];
  public viewingBuilding: boolean = false;
  public viewingSatelliteLocation: boolean = false;
  public showCreateLicense: boolean = false;
  public validationErrors: string[] = [];
  public loading: boolean = false;
  public licenseRequests: ILicenseRequest[] = [];

  public license: IHospitalLicense = {
    id: 0,
    userId: '',
    licenseId: '',
    enteredByStaff: false,
    assignedToId: '',
    assignedToName: '',
    finalApprovalBy: '',
    canRenew: false,
    isRenewal: false,
    electronicSignature: '',
    signedDate: '',
    adminPrintTitle: '',
    applicationDate: '',
    approvedDate: '',
    deniedDate: '',
    expirationDate: '',
    entityName: '',
    hospitalDBAName: '',
    hospitalPhone: '',
    hospitalEmail: '',
    hospitalFax: '',
    classification: '',
    mainHospitalOutOfState: null,
    hasUnionRepresentation: null,
    existingLicense: '',
    accredited: null,
    accreditingAgency: '',
    deemed: null,
    fiscalYearEnd: '',
    ownershipCategory: '',
    isSicf: null,
    bypassDuplicateManagerValidation: false,
    hospitalAdminName: '',
    hospitalAdminPhone: '',
    hospitalAdminEmail: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    chiefNurseName: '',
    chiefNursePhone: '',
    chiefNurseEmail: '',
    filerName: '',
    filerPhone: '',
    filerEmail: '',
    nurseManagerName: '',
    nurseManagerTitle: '',
    nurseManagerEmail: '',
    nurseManagerPhone: '',
    directCareName: '',
    directCareTitle: '',
    directCareEmail: '',
    directCarePhone: '',
    emergencyDepartmentDirectorName: '',
    emergencyDepartmentDirectorPhone: '',
    emergencyDepartmentDirectorEmail: '',
    technicalManagerCoChairName: '',
    technicalManagerCoChairPhone: '',
    technicalManagerCoChairEmail: '',
    technicalStaffCoChairName: '',
    technicalStaffCoChairPhone: '',
    technicalStaffCoChairEmail: '',
    serviceStaffingCommitteeManagerCoChairName: '',
    serviceStaffingCommitteeManagerCoChairPhone: '',
    serviceStaffingCommitteeManagerCoChairEmail: '',
    serviceStaffingCommitteeStaffCoChairName: '',
    serviceStaffingCommitteeStaffCoChairPhone: '',
    serviceStaffingCommitteeStaffCoChairEmail: '',
    inpatientBeds: null,
    inpatientPsychiatricBeds: null,
    satelliteInpatientPsychiatricBeds: null,
    swingBeds: null,
    swingBedsCount: null,
    maternity: false,
    surgical: false,
    emergency: false,
    backgroundCheckDate: '',
    statuses: [],
    representativeContacts: []
  }

  public licenses: IHospitalLicense[] = [];

  private readonly outOfStateOptionalControls = [
    'nurseManagerName',
    'nurseManagerTitle',
    'nurseManagerEmail',
    'directCareName',
    'directCareTitle',
    'directCareEmail',
    'emergencyDepartmentDirectorName',
    'emergencyDepartmentDirectorPhone',
    'emergencyDepartmentDirectorEmail',
    'technicalManagerCoChairName',
    'technicalManagerCoChairPhone',
    'technicalManagerCoChairEmail',
    'technicalStaffCoChairName',
    'technicalStaffCoChairPhone',
    'technicalStaffCoChairEmail',
    'serviceStaffingCommitteeManagerCoChairName',
    'serviceStaffingCommitteeManagerCoChairPhone',
    'serviceStaffingCommitteeManagerCoChairEmail',
    'serviceStaffingCommitteeStaffCoChairName',
    'serviceStaffingCommitteeStaffCoChairPhone',
    'serviceStaffingCommitteeStaffCoChairEmail'
  ];

  public classificationForm = new FormGroup({
    isSicf: new FormControl<boolean | null>(null, Validators.required),
    classification: new FormControl('', Validators.required)
  });

  public licenseForm = new FormGroup({
    hospitalDBAName: new FormControl(''),
    hospitalPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    hospitalEmail: new FormControl('', [Validators.required, Validators.email]),
    hospitalFax: new FormControl('', [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    existingLicense: new FormControl(''),
    accredited: new FormControl<boolean | null>(null, Validators.required),
    deemed: new FormControl<boolean | null>(null, Validators.required),
    accreditingAgency: new FormControl('', Validators.required),
    fiscalYearEnd: new FormControl('', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/)]),
    ownershipCategory: new FormControl('', Validators.required),
    entityName: new FormControl('', Validators.required),
    }, {
      validators: [
        this.accreditingAgencyValidator(),
        this.deemedValidator()
      ]
  });

  public readonly contactNameErrorMatchers = Object.fromEntries(
    Object.keys(hospitalContactNameLabels).map(field => [field, new HospitalContactNameErrorStateMatcher(field)])
  );

  public contactForm = new FormGroup({
    mainHospitalOutOfState: new FormControl<boolean | null>(null, Validators.required),
    hasUnionRepresentation: new FormControl<boolean | null>(null, Validators.required),
    emergencyContactName: new FormControl('', Validators.required),
    emergencyContactPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyContactEmail: new FormControl('', [Validators.required, Validators.email]),
    hospitalAdminName: new FormControl('', Validators.required),
    hospitalAdminPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    hospitalAdminEmail: new FormControl('', [Validators.required, Validators.email]),
    chiefNurseName: new FormControl('', {
      validators: Validators.required,
      asyncValidators: [this.chiefNurseValidator.validate.bind(this.chiefNurseValidator)],
      updateOn: 'blur'
    }),
    chiefNursePhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    chiefNurseEmail: new FormControl('', [Validators.required, Validators.email]),
    filerName: new FormControl('', Validators.required),
    filerPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    filerEmail: new FormControl('', [Validators.required, Validators.email]),
    nurseManagerName: new FormControl('', Validators.required),
    nurseManagerTitle: new FormControl('', Validators.required),
    nurseManagerEmail: new FormControl('', [Validators.required, Validators.email]),
    nurseManagerPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    directCareName: new FormControl('', [Validators.required]),
    directCareTitle: new FormControl('', Validators.required),
    directCareEmail: new FormControl('', [Validators.required, Validators.email]),
    directCarePhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyDepartmentDirectorName: new FormControl('', Validators.required),
    emergencyDepartmentDirectorPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyDepartmentDirectorEmail: new FormControl('', [Validators.required, Validators.email]),
    technicalManagerCoChairName: new FormControl('', Validators.required),
    technicalManagerCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    technicalManagerCoChairEmail: new FormControl('', [Validators.required, Validators.email]),
    technicalStaffCoChairName: new FormControl('', Validators.required),
    technicalStaffCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    technicalStaffCoChairEmail: new FormControl('', [Validators.required, Validators.email]),
    serviceStaffingCommitteeManagerCoChairName: new FormControl('', Validators.required),
    serviceStaffingCommitteeManagerCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    serviceStaffingCommitteeManagerCoChairEmail: new FormControl('', [Validators.required, Validators.email]),
    serviceStaffingCommitteeStaffCoChairName: new FormControl('', [Validators.required]),
    serviceStaffingCommitteeStaffCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    serviceStaffingCommitteeStaffCoChairEmail: new FormControl('', [Validators.required, Validators.email])
  }, {
    validators: hospitalContactNamesValidator(() => this.license.bypassDuplicateManagerValidation === true)
  });

  public servicesForm = new FormGroup({
    inpatientBeds: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    inpatientPsychiatricBeds: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    satelliteInpatientPsychiatricBeds: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    swingBeds: new FormControl<boolean | null>(null, Validators.required),
    maternity: new FormControl(false),
    surgical: new FormControl(false),
    emergency: new FormControl(false),
  }, {
      validators: [
        this.swingBedsValidator()
      ]
    });

  public signatureForm = new FormGroup({
    electronicSignature: new FormControl('', Validators.required),
    signedDate: new FormControl<Date | null>(null, Validators.required),
    adminPrintTitle: new FormControl('', Validators.required)
  });

  public requestForm = new FormGroup({
    owner: new FormControl('', Validators.required),
    licenseNumber: new FormControl('', {
      asyncValidators: [this.licenseValidator.validate.bind(this.licenseValidator)],
      updateOn: 'blur'
    }),
    ownerChange: new FormControl(false),
    signature: new FormControl(''),
    dateSigned: new FormControl(''),
    street: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    zip: new FormControl('', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)])
  }, {
    validators: [
      this.licenseRequestSignatureValidator(),
      this.licenseRequestDateValidator()
    ]
  });

  public editingRepresentativeContact: IRepresentativeContact | null = null;
  public showRepresentativeContactForm: boolean = false;

  public representativeContactColumns: string [] = ['representativeType', 'representativeStaff', 'contactName', 'contactPhone', 'contactEmail', 'actions'];
  public representativeContactDataSource = new MatTableDataSource<IRepresentativeContact>(this.license.representativeContacts);
  @ViewChild('representativeContactPaginator', {static: false}) representativeContactPaginator: MatPaginator | undefined;
  @ViewChild('representativeContactSort', {static: false}) representativeContactSort: MatSort | undefined;

  public representativeTypeOptions: string[] = [
    'Oregon Nurses Association (ONA)',
    'Oregon Federation of Nurses and Healthcare Professionals (OFNHP)',
    'Service Employees International Union (SEIU)',
    'American Federation of State, County and Municipal Employees (AFSCME)',
    'Other'
  ];

  public representativeContactForm = new FormGroup({
    representativeType: new FormControl('', Validators.required),
    representativeTypeOther: new FormControl(''),
    representativeStaff: new FormControl('', Validators.required),
    contactName: new FormControl('', Validators.required),
    contactPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    contactEmail: new FormControl('', [Validators.required, Validators.email])
  }, {
    validators: [this.representativeTypeOtherValidator()]
  });

  ngOnInit(): void {
    this.getLicenses();
    this.configureOutOfStateValidation();

    this.sharedService.setUserTheme();

    this.representativeContactForm.get('representativeType')?.valueChanges.subscribe(value => {
      const otherControl = this.representativeContactForm.get('representativeTypeOther');
      if (value === 'Other') {
        otherControl?.setValidators([Validators.required]);
      } else {
        otherControl?.clearValidators();
      }
      otherControl?.updateValueAndValidity();
    });
  }

  ngOnDestroy(): void {
    // Route deactivation waits for pending saves before destroying editors.
  }

  private configureOutOfStateValidation(): void {
    const outOfStateControl = this.contactForm.controls.mainHospitalOutOfState;
    outOfStateControl.valueChanges
      .pipe(
        startWith(outOfStateControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(isOutOfState => {
        this.outOfStateOptionalControls.forEach(controlName => {
          const control = this.contactForm.get(controlName);

          if (isOutOfState === true) {
            control?.removeValidators(Validators.required);
          } else {
            control?.addValidators(Validators.required);
          }

          control?.updateValueAndValidity({ emitEvent: false });
        });
      });
  }

  getLicenses(): void {
    this.loading = true;
    this.licenseService.getLicenses().subscribe({
      next: response => this.licenses = response,
      error: e => {
        console.log('error getting Hospital licenses:', e);
        this.sharedService.toastr.error(e);
      },
      complete: () => {
        this.getLicenseRequests();
        this.showCreateLicense = this.licenses.length === 0 && this.licenseRequests.length === 0;
        this.loading = false;
      }
    });
  }

  getLicenseRequests(): void {
    this.licenseService.getLicenseRequests().subscribe({
      next: response => this.licenseRequests = response,
      error: e => {
        console.log('error getting license requests', e);
        this.sharedService.toastr.error('Error getting license requests');
      }
    });
  }

  cancelLicenseRequest(request: ILicenseRequest): void {
    this.sharedService.openConfirm("Cancel this license request?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.cancelLicenseRequest(request.id).subscribe({
            complete: () => {
              this.sharedService.toastr.success('License request canceled successfully.');
              this.licenseRequests = this.licenseRequests.filter(r => r.id != request.id);
            },
            error: e => {
              console.log('error canceling license request', e);
              this.sharedService.toastr.error('Error canceling license request');
            }
          });
        }
      }
    );
  }

  createLicenseRequest(): void {
    let form = this.requestForm.getRawValue();
    let request: ILicenseRequest = {
      id: 0,
      owner: form.owner,
      licenseNumber: form.licenseNumber,
      entityType: '',
      ownerChange: false,
      isSubunit: false,
      parentId: '',
      email: this.authService.email,
      requestData: '',
      signature: '',
      dateSigned: '',
      approved: null,
      denied: null,
      deniedReason: '',
      finalDeterminationBy: '',
      finalDeterminationDate: ''
    };

    let address = {
      street: form.street,
      city: form.city,
      state: form.state,
      zip: form.zip
    };

    request.requestData = JSON.stringify(address);
    this.licenseService.requestLicense(request).subscribe({
      next: response => request.id = response,
      complete: () => {
        this.sharedService.toastr.success('License request submitted successfully.');
        this.licenseRequests.push(request);
        this.requestForm.reset({
          owner: '',
          licenseNumber: '',
          street: '',
          city: '',
          state: '',
          zip: '',
          signature: '',
          dateSigned: '',
          ownerChange: false
        });
      },
      error: e => {
        console.log('error requesting license', e);
        this.sharedService.toastr.error('Error requesting license');
      }
    });
  }

  updateLicenseForm(license: IHospitalLicense): void {
    this.classificationForm.reset({
      classification: license.classification,
      isSicf: license.isSicf
    });

    this.licenseForm.reset({
      hospitalDBAName: license.hospitalDBAName,
      hospitalPhone: license.hospitalPhone,
      hospitalEmail: license.hospitalEmail,
      hospitalFax: license.hospitalFax,
      existingLicense: license.existingLicense,
      accredited: license.accredited,
      accreditingAgency: license.accreditingAgency,
      deemed: license.deemed,
      fiscalYearEnd: license.fiscalYearEnd,
      ownershipCategory: license.ownershipCategory ? JSON.parse(license.ownershipCategory) : null,
      entityName: license.entityName,
    });

    this.contactForm.reset({
      mainHospitalOutOfState: license.mainHospitalOutOfState,
      hasUnionRepresentation: license.hasUnionRepresentation,
      hospitalAdminName: license.hospitalAdminName,
      hospitalAdminPhone: license.hospitalAdminPhone,
      hospitalAdminEmail: license.hospitalAdminEmail,
      emergencyContactName: license.emergencyContactName,
      emergencyContactPhone: license.emergencyContactPhone,
      emergencyContactEmail: license.emergencyContactEmail,
      chiefNurseName: license.chiefNurseName,
      chiefNursePhone: license.chiefNursePhone,
      chiefNurseEmail: license.chiefNurseEmail,
      filerName: license.filerName,
      filerPhone: license.filerPhone,
      filerEmail: license.filerEmail,
      nurseManagerName: license.nurseManagerName,
      nurseManagerTitle: license.nurseManagerTitle,
      nurseManagerEmail: license.nurseManagerEmail,
      nurseManagerPhone: license.nurseManagerPhone,
      directCareName: license.directCareName,
      directCareTitle: license.directCareTitle,
      directCareEmail: license.directCareEmail,
      directCarePhone: license.directCarePhone,
      emergencyDepartmentDirectorName: license.emergencyDepartmentDirectorName,
      emergencyDepartmentDirectorPhone: license.emergencyDepartmentDirectorPhone,
      emergencyDepartmentDirectorEmail: license.emergencyDepartmentDirectorEmail,
      technicalManagerCoChairName: license.technicalManagerCoChairName,
      technicalManagerCoChairPhone: license.technicalManagerCoChairPhone,
      technicalManagerCoChairEmail: license.technicalManagerCoChairEmail,
      technicalStaffCoChairName: license.technicalStaffCoChairName,
      technicalStaffCoChairPhone: license.technicalStaffCoChairPhone,
      technicalStaffCoChairEmail: license.technicalStaffCoChairEmail,
      serviceStaffingCommitteeManagerCoChairName: license.serviceStaffingCommitteeManagerCoChairName,
      serviceStaffingCommitteeManagerCoChairPhone: license.serviceStaffingCommitteeManagerCoChairPhone,
      serviceStaffingCommitteeManagerCoChairEmail: license.serviceStaffingCommitteeManagerCoChairEmail,
      serviceStaffingCommitteeStaffCoChairName: license.serviceStaffingCommitteeStaffCoChairName,
      serviceStaffingCommitteeStaffCoChairPhone: license.serviceStaffingCommitteeStaffCoChairPhone,
      serviceStaffingCommitteeStaffCoChairEmail: license.serviceStaffingCommitteeStaffCoChairEmail
    });

    this.servicesForm.reset({
      inpatientBeds: license.inpatientBeds,
      inpatientPsychiatricBeds: license.inpatientPsychiatricBeds,
      satelliteInpatientPsychiatricBeds: license.satelliteInpatientPsychiatricBeds,
      swingBeds: license.swingBeds,
      maternity: license.maternity,
      surgical: license.surgical,
      emergency: license.emergency
    });

    this.signatureForm.reset({
      electronicSignature: license.electronicSignature,
      adminPrintTitle: license.adminPrintTitle
    });

    if(license.signedDate){
      this.signatureForm.get('signedDate')?.setValue(new Date(license.signedDate));
    }

    if(license.id > 0){
      if(this.locked) {
        this.licenseForm.disable();
        this.classificationForm.disable();
        this.servicesForm.disable();
        this.contactForm.disable();
      }
      else{
        this.licenseForm.enable();
        this.classificationForm.enable();
        this.servicesForm.enable();
        this.contactForm.enable();
      }
    }

    if(this.license.isRenewal && license.id > 0){
      this.disableRenewalForms();
    }
  }

  disableRenewalForms(): void {
    this.classificationForm.disable();
    this.licenseForm.get('entityName')?.disable();
    this.licenseForm.get('hospitalDBAName')?.disable();
    this.licenseForm.get('hospitalEmail')?.disable();
    this.servicesForm.get('inpatientBeds')?.disable();
    // this.servicesForm.get('inpatientPsychiatricBeds')?.disable();
    // this.servicesForm.get('satelliteInpatientPsychiatricBeds')?.disable();
    // this.servicesForm.get('swingBeds')?.disable();
  }

  updateLicense(): void {
    let classificationFormValue = this.classificationForm.getRawValue();
    this.license.classification = classificationFormValue.classification;
    let form = this.licenseForm.getRawValue();
    this.license.entityName = form.entityName;
    this.license.hospitalDBAName = form.hospitalDBAName;
    this.license.hospitalPhone = form.hospitalPhone;
    this.license.hospitalEmail = form.hospitalEmail;
    this.license.hospitalFax = form.hospitalFax;
    this.license.existingLicense = form.existingLicense;
    this.license.accredited = form.accredited;
    this.license.accreditingAgency = form.accreditingAgency;
    this.license.deemed = form.deemed;
    this.license.fiscalYearEnd = form.fiscalYearEnd;
    this.license.isSicf = classificationFormValue.isSicf;
    if (Array.isArray(form.ownershipCategory)) {
      this.license.ownershipCategory = JSON.stringify(form.ownershipCategory);
    } else {
      this.license.ownershipCategory = form.ownershipCategory;
    }

    let contactForm = this.contactForm.getRawValue();
    this.license.mainHospitalOutOfState = contactForm.mainHospitalOutOfState;
    this.license.hasUnionRepresentation = contactForm.hasUnionRepresentation;
    this.license.hospitalAdminName = contactForm.hospitalAdminName;
    this.license.hospitalAdminPhone = contactForm.hospitalAdminPhone;
    this.license.hospitalAdminEmail = contactForm.hospitalAdminEmail;
    this.license.emergencyContactName = contactForm.emergencyContactName;
    this.license.emergencyContactPhone = contactForm.emergencyContactPhone;
    this.license.emergencyContactEmail = contactForm.emergencyContactEmail;
    this.license.chiefNurseName = contactForm.chiefNurseName;
    this.license.chiefNursePhone = contactForm.chiefNursePhone;
    this.license.chiefNurseEmail = contactForm.chiefNurseEmail;
    this.license.filerName = contactForm.filerName;
    this.license.filerPhone = contactForm.filerPhone;
    this.license.filerEmail = contactForm.filerEmail;
    this.license.nurseManagerName = contactForm.nurseManagerName;
    this.license.nurseManagerTitle = contactForm.nurseManagerTitle;
    this.license.nurseManagerEmail = contactForm.nurseManagerEmail;
    this.license.nurseManagerPhone = contactForm.nurseManagerPhone;
    this.license.directCareName = contactForm.directCareName;
    this.license.directCareTitle = contactForm.directCareTitle;
    this.license.directCareEmail = contactForm.directCareEmail;
    this.license.directCarePhone = contactForm.directCarePhone;
    this.license.emergencyDepartmentDirectorName = contactForm.emergencyDepartmentDirectorName;
    this.license.emergencyDepartmentDirectorPhone = contactForm.emergencyDepartmentDirectorPhone;
    this.license.emergencyDepartmentDirectorEmail = contactForm.emergencyDepartmentDirectorEmail;
    this.license.technicalManagerCoChairName = contactForm.technicalManagerCoChairName;
    this.license.technicalManagerCoChairPhone = contactForm.technicalManagerCoChairPhone;
    this.license.technicalManagerCoChairEmail = contactForm.technicalManagerCoChairEmail;
    this.license.technicalStaffCoChairName = contactForm.technicalStaffCoChairName;
    this.license.technicalStaffCoChairPhone = contactForm.technicalStaffCoChairPhone;
    this.license.technicalStaffCoChairEmail = contactForm.technicalStaffCoChairEmail;
    this.license.serviceStaffingCommitteeManagerCoChairName = contactForm.serviceStaffingCommitteeManagerCoChairName;
    this.license.serviceStaffingCommitteeManagerCoChairPhone = contactForm.serviceStaffingCommitteeManagerCoChairPhone;
    this.license.serviceStaffingCommitteeManagerCoChairEmail = contactForm.serviceStaffingCommitteeManagerCoChairEmail;
    this.license.serviceStaffingCommitteeStaffCoChairName = contactForm.serviceStaffingCommitteeStaffCoChairName;
    this.license.serviceStaffingCommitteeStaffCoChairPhone = contactForm.serviceStaffingCommitteeStaffCoChairPhone;
    this.license.serviceStaffingCommitteeStaffCoChairEmail = contactForm.serviceStaffingCommitteeStaffCoChairEmail;

    let servicesForm = this.servicesForm.getRawValue();
    this.license.inpatientBeds = servicesForm.inpatientBeds;
    this.license.inpatientPsychiatricBeds = servicesForm.inpatientPsychiatricBeds;
    this.license.satelliteInpatientPsychiatricBeds = servicesForm.satelliteInpatientPsychiatricBeds;
    this.license.swingBeds = servicesForm.swingBeds;
    this.license.maternity = servicesForm.maternity;
    this.license.surgical = servicesForm.surgical;
    this.license.emergency = servicesForm.emergency;
  }

  selectLicense(license: IHospitalLicense): void {
    this.license = license;
    this.chiefNurseValidator.licenseId = license.licenseId ?? '';
    this.contactForm.get('chiefNurseName')?.updateValueAndValidity();
    this.updateLicenseForm(license);
    this.updateRepresentativeContactsTable();
  }

  async closeLicense(): Promise<void> {
    if (!await this.canLeave()) return;
    this.license = this.getEmptyLicense();
    this.chiefNurseValidator.licenseId = this.license.licenseId ?? '';
    this.updateLicenseForm(this.license);
  }

  getEmptyLicense(): IHospitalLicense {
    return {
      id: 0,
      userId: '',
      licenseId: '',
      enteredByStaff: false,
      assignedToId: '',
      assignedToName: '',
      finalApprovalBy: '',
      canRenew: false,
      isRenewal: false,
      electronicSignature: '',
      signedDate: '',
      adminPrintTitle: '',
      applicationDate: '',
      approvedDate: '',
      deniedDate: '',
      expirationDate: '',
      entityName: '',
      hospitalDBAName: '',
      hospitalPhone: '',
      hospitalEmail: '',
      hospitalFax: '',
      classification: '',
      mainHospitalOutOfState: null,
      hasUnionRepresentation: null,
      existingLicense: '',
      accredited: null,
      accreditingAgency: '',
      deemed: null,
      fiscalYearEnd: '',
      ownershipCategory: '',
      isSicf: null,
      bypassDuplicateManagerValidation: false,
      hospitalAdminName: '',
      hospitalAdminPhone: '',
      hospitalAdminEmail: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactEmail: '',
      chiefNurseName: '',
      chiefNursePhone: '',
      chiefNurseEmail: '',
      filerName: '',
      filerPhone: '',
      filerEmail: '',
      nurseManagerName: '',
      nurseManagerTitle: '',
      nurseManagerPhone: '',
      nurseManagerEmail: '',
      directCarePhone: '',
      directCareName: '',
      directCareTitle: '',
      directCareEmail: '',
      emergencyDepartmentDirectorName: '',
      emergencyDepartmentDirectorPhone: '',
      emergencyDepartmentDirectorEmail: '',
      technicalManagerCoChairName: '',
      technicalManagerCoChairPhone: '',
      technicalManagerCoChairEmail: '',
      technicalStaffCoChairName: '',
      technicalStaffCoChairPhone: '',
      technicalStaffCoChairEmail: '',
      serviceStaffingCommitteeManagerCoChairName: '',
      serviceStaffingCommitteeManagerCoChairPhone: '',
      serviceStaffingCommitteeManagerCoChairEmail: '',
      serviceStaffingCommitteeStaffCoChairName: '',
      serviceStaffingCommitteeStaffCoChairPhone: '',
      serviceStaffingCommitteeStaffCoChairEmail: '',
      inpatientBeds: null,
      inpatientPsychiatricBeds: null,
      satelliteInpatientPsychiatricBeds: null,
      swingBeds: null,
      swingBedsCount: null,
      maternity: false,
      surgical: false,
      emergency: false,
      backgroundCheckDate: '',
      statuses: [],
      representativeContacts: []
    }
  }

  async saveLicense(): Promise<boolean> {
    if (!await this.saveLocations()) return false;
    this.updateLicense();
    try {
      const saved = await firstValueFrom(this.licenseService.updateLicense(this.license));
      if (saved === false) throw new Error('Save failed');
      return true;
    } catch {
      this.sharedService.toastr.error('Could not save application. Your edits are still here.');
      return false;
    }
  }

  async submitLicense(): Promise<void> {
    if (this.submitting || this.validatingLocations) return;
    this.submitting = true;
    try {
      if (!await this.saveLicense()) return;
      const errors = await this.validateLocationDrafts();
      if (errors.length) {
        this.validationErrors = [...new Set([...this.validationErrors, ...errors])];
        return;
      }
      const form = this.signatureForm.getRawValue();
      this.license.electronicSignature = form.electronicSignature;
      this.license.signedDate = form.signedDate ? form.signedDate.toDateString() : '';
      this.license.adminPrintTitle = form.adminPrintTitle;
      const submitted = await firstValueFrom(this.licenseService.submitLicense(this.license));
      if (submitted === false) throw new Error('Submission failed');
      this.sharedService.toastr.success('Application Submitted');
      const license = this.licenses.find(item => item.id === this.license.id);
      if (license) {
        license.statuses = license.statuses.filter(status => status.label !== 'Started');
        license.statuses.push({ label: 'Submitted', archived: false, staffOnly: false });
      }
    } catch { this.sharedService.toastr.error('Could not submit application'); }
    finally { this.submitting = false; }
  }

  clearClassification(): void {
    this.classificationForm.get('classification')?.reset();
  }

  renewLicense(licenseId: string): void {
    this.sharedService.openConfirm("Begin your renewal?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.renewLicense(licenseId).subscribe({
            next: response => {
              this.licenses.push(response)
              this.selectLicense(response);
            },
            error: e => {
              console.log('error renewing license', e);
              this.sharedService.toastr.error("Could Not Renew License");
            }
          });
        }
      }
    );
  }

  toggleViewingOnCampusBuildings(): void {
    this.viewingBuilding = !this.viewingBuilding;
  }

  toggleViewingSatelliteLocation(): void {
    this.viewingSatelliteLocation = !this.viewingSatelliteLocation;
  }

  async changeStep(step: StepperSelectionEvent): Promise<void> {
    if (this.savingStep) return;
    if (!this.locked && !this.approved && step.previouslySelectedIndex === 5 &&
        !this.classificationForm.get('isSicf')?.value) {
      this.savingStep = true;
      try {
        // selectionChange fires before the stepper updates its selected index.
        // Restore the current step, then advance only after persistence succeeds.
        await Promise.resolve();
        this.stepper.selectedIndex = step.previouslySelectedIndex;
        if (!await this.saveLocations()) return;
        this.stepper.selectedIndex = step.selectedIndex;
      } finally { this.savingStep = false; }
    }
    this.processStepChange(step);
  }

  private processStepChange(step: StepperSelectionEvent): void {
    if(this.approved || this.locked && !this.license.isRenewal){
      return;
    }

    if(step.previouslySelectedIndex === 0){
      this.saveLicense();
    }

    var isScif = this.classificationForm.get('isSicf')?.value;

    if(step.previouslySelectedIndex === 1){
      this.licenseForm.markAllAsTouched();
      this.validateAccreditingAgency();
      this.validateDeemed();
      this.saveLicense();
      this.addresses.cleanup();
    }

    if(step.previouslySelectedIndex === 2){
      if(this.owners.managingOwner){
        this.owners.saveOwner();
      }
    }

    if(step.selectedIndex === 3){
      if(isScif)
      {
        this.contactForm.get('mainHospitalOutOfState')?.setValue(false);
      }
    }

    if(step.previouslySelectedIndex === 3){
      this.saveLicense();
      this.contactForm.markAllAsTouched();
      this.updateRepresentativeContactsTable();
    }

    if(step.previouslySelectedIndex === 4){
      this.saveLicense();
      this.servicesForm.markAllAsTouched();
    }

    if(step.selectedIndex === 4){
      if(isScif)
      {
        this.servicesForm.get('inpatientPsychiatricBeds')?.setValue(0);
        this.servicesForm.get('satelliteInpatientPsychiatricBeds')?.setValue(0);
      }
    }

    var submitIndex = isScif ? 5 : 6;

    if(step.selectedIndex === submitIndex){
      this.validationErrors = [];
      this.validatingLocations = true;
      this.validateLocationDrafts().then(errors => this.validationErrors.push(...errors))
        .finally(() => { this.validatingLocations = false; });
      this.validateSwingBeds();
      this.validateOnCampusBuildings();
      this.validateSatelliteLocation();
      this.validateOwners();
      this.validateAddresses();
      this.validateDocuments();
      this.validateChiefNurseName();
    }
  }

  downloadReferenceDocument(documentType: string): void {
    this.documents.toArray()[0].getReferenceDocument(documentType);
  }

  validateAddresses(): void {
    let errors: string[] = [];

    if (!this.addresses.addresses.find(a =>
      !a.archived && a.addressTypes.find(t => t.type === 'Physical')
    )) {
      errors.push('Hospital must have a physical address');
    }
    this.validationErrors.push(...errors);
  }

  validateOnCampusBuildings(): void {
    if(this.classificationForm.get('isSicf')?.value) return;
    this.onCampusBuildings.validateBuildingAddresses().then(errors => {
      this.validationErrors.push(...errors);
    });
    // this.onCampusBuildings.validateBuildingOfficeHours().then(errors => {
    //   this.validationErrors.push(...errors);
    // });
  }

  validateSatelliteLocation(): void {
    if(this.classificationForm.get('isSicf')?.value) return;
    this.satelliteLocation.validateSatelliteAddresses().then(errors => {
      this.validationErrors.push(...errors);
    });
    // this.satelliteLocation.validateSatelliteOfficeHours().then(errors => {
    //   this.validationErrors.push(...errors);
    // });
    // this.satelliteLocation.validateDocuments().then(errors => {
    //   this.validationErrors.push(...errors);
    // });
  }

  validateOwners(): void {
    if(this.owners.owners.length === 0){
      this.validationErrors.push('No owner information provided');
    }

    this.validationErrors.push(...this.owners.validateAddresses());
    this.validationErrors.push(...this.owners.validateDocuments());
  }

  validateDocuments(filesUpdated: boolean = false): void {
    if(filesUpdated){
      this.validationErrors = this.validationErrors.filter(error => error !== 'Missing Admin Signature document');
    }
    let errors: string[] = [];
    let types: string[] = [];
    this.documents.toArray().forEach((document) => {
      let documentSet: IDocument[] = document.documents;
      documentSet.forEach((document) => {
        document.types.forEach((type) => {
          types.push(type.type);
        });
      });
    });

    if(!types.includes('Admin Signature')){
      errors.push('Missing Admin Signature document');
    }

    this.validationErrors.push(...errors);
  }

  validateChiefNurseName(): void {
    if (this.contactForm.get('chiefNurseName')?.hasError('invalidChiefNurseName')) {
      this.validationErrors.push('Chief Nursing Officer/Nurse Executive matches an existing Chief Nursing Officer/Nurse Executive or Hospital Administrator');
    }
  }

  licenseIsApproved(statuses: IStatus[]): boolean{
    return statuses.some(s => s.label === this.sharedService.approved);
  }

  public get locked(): boolean {
    return !this.license.statuses.some(s => s.label === this.sharedService.started);
  }

  public get approved(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.approved);
  }

  canRenew(license: IHospitalLicense): boolean {
    if(!license.expirationDate || license.isRenewal || this.licenses.find(l => l.licenseId === license.licenseId && l.isRenewal)){
      return false;
    }

    let expiration = new Date(license.expirationDate);
    let expirationDateWindow = new Date();
    let expirationWindow = expirationDateWindow.getDate() + 90;
    expirationDateWindow.setDate(expirationWindow);
    return expiration <= expirationDateWindow;
  }

  canSelect(license: IHospitalLicense): boolean {
    if(this.licenses.find(l => l.licenseId === license.licenseId && l.isRenewal && l.id !== license.id)){
      return false;
    }
    return true;
  }

  accreditingAgencyValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const accredited = control.value.accredited;
      const accreditingAgency = control.value.accreditingAgency

      if(accredited == null || accredited == false){
        return null;
      }
      return (accreditingAgency != '' && accreditingAgency != null) ? null : { accreditingAgencyRequired: true };
    }
  }

  validateAccreditingAgency(): boolean {
    if (this.licenseForm.hasError('accreditingAgencyRequired') && this.licenseForm.get('accreditingAgency')?.touched) {
      this.licenseForm.get('accreditingAgency')?.setErrors(['accreditingAgencyRequired']);
      return true;
    }
    this.licenseForm.get('accreditingAgency')?.clearValidators();
    this.licenseForm.get('accreditingAgency')?.updateValueAndValidity();
    return false;
  }

  deemedValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const accredited = control.value.accredited;
      const deemed = control.value.deemed;

      if(accredited == null || accredited == false){
        return null;
      }
      return (deemed == false || deemed == true) ? null : { deemedRequired: true };
    }
  }

  validateDeemed(): boolean {
    if (this.licenseForm.hasError('deemedRequired') && this.licenseForm.get('deemed')?.touched) {
      this.licenseForm.get('deemed')?.setErrors(['deemedRequired']);
      return true;
    }
    this.licenseForm.get('deemed')?.clearValidators();
    this.licenseForm.get('deemed')?.updateValueAndValidity();
    return false;
  }

  swingBedsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isSicf = this.classificationForm.get('isSicf')?.value;
      if(isSicf) return null;

      const swingBedsControl = control.get('swingBeds');
      if(swingBedsControl?.disabled) return null;

      const swingBeds = swingBedsControl?.value;
      const classification = this.classificationForm.get('classification')?.value;

      if(swingBeds == true || swingBeds == false){
        return null;
      }

      if(classification === "Low Occupancy Acute Care Hospital" || classification == null || classification === ''){
        return null;
      }

      return { swingBedsRequired: true };
    }
  }

  validateSwingBeds(): boolean {
    if(this.servicesForm.get('swingBeds')?.disabled) return false;
    if (this.servicesForm.hasError('swingBedsRequired') && this.servicesForm.get('swingBeds')?.touched) {
      this.servicesForm.get('swingBeds')?.setErrors(['swingBedsRequired']);
      return true;
    }
    this.servicesForm.get('swingBeds')?.clearValidators();
    this.servicesForm.get('swingBeds')?.updateValueAndValidity();
    return false;
  }

  licenseRequestSignatureValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const ownerChange = control.value.ownerChange;
      const signature = control.value.signature;

      if(!ownerChange){
        return null;
      }
      return signature ? null : { signatureRequired: true };
    }
  }

  validateLicenseRequestSignature(): boolean {
    if (this.requestForm.hasError('signatureRequired') && this.requestForm.get('signature')?.touched) {
      this.requestForm.get('signature')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  licenseRequestDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const ownerChange = control.value.ownerChange;
      const dateSigned = control.value.dateSigned;

      if(!ownerChange){
        return null;
      }
      return dateSigned ? null : { dateRequired: true };
    }
  }

  validateLicenseRequestDate(): boolean {
    if (this.requestForm.hasError('dateRequired') && this.requestForm.get('dateSigned')?.touched) {
      this.requestForm.get('dateSigned')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  representativeTypeOtherValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const representativeType = control.value.representativeType;
      const representativeTypeOther = control.value.representativeTypeOther;

      if (representativeType === 'Other' && (!representativeTypeOther || representativeTypeOther.trim() === '')) {
        return { representativeTypeOtherRequired: true };
      }
      return null;
    }
  }

  // Representative Contacts

  updateRepresentativeContactsTable(): void {
    this.representativeContactDataSource.data = [...this.license.representativeContacts];
    setTimeout(() => {
      if (this.representativeContactPaginator) {
        this.representativeContactDataSource.paginator = this.representativeContactPaginator;
      }
      if (this.representativeContactSort) {
        this.representativeContactDataSource.sort = this.representativeContactSort;
      }
    });
  }

  showAddRepresentativeContactForm(): void {
    this.showRepresentativeContactForm = true;
    this.editingRepresentativeContact = null;
    this.representativeContactForm.reset();
  }

  saveRepresentativeContact(): void {
    if (this.representativeContactForm.invalid) {
      return;
    }

    const formValue = this.representativeContactForm.value;
    const representativeType = formValue.representativeType === 'Other'
      ? formValue.representativeTypeOther
      : formValue.representativeType;

    if (this.editingRepresentativeContact) {
      const updatedContact: IRepresentativeContact = {
        ...this.editingRepresentativeContact,
        representativeType: representativeType || null,
        representativeStaff: formValue.representativeStaff || null,
        contactName: formValue.contactName || null,
        contactPhone: formValue.contactPhone || null,
        contactEmail: formValue.contactEmail || null
      };

      this.licenseService.updateRepresentativeContact(updatedContact).subscribe({
        next: () => {
          const index = this.license.representativeContacts.findIndex(c => c.id === this.editingRepresentativeContact!.id);
          if (index !== -1) {
            this.license.representativeContacts[index] = updatedContact;
          }
          this.updateRepresentativeContactsTable();
          this.sharedService.toastr.success('Representative contact updated successfully');
          this.editingRepresentativeContact = null;
          this.representativeContactForm.reset();
          this.showRepresentativeContactForm = false;
        },
        error: (e) => {
          console.log('error updating representative contact', e);
          this.sharedService.toastr.error('Error updating representative contact');
        }
      });
    } else {
      const newContact: IRepresentativeContact = {
        id: 0,
        userId: this.authService.email,
        representativeType: representativeType || null,
        representativeStaff: formValue.representativeStaff || null,
        contactName: formValue.contactName || null,
        contactPhone: formValue.contactPhone || null,
        contactEmail: formValue.contactEmail || null,
        hospitalLicenseId: this.license.id
      };

      this.licenseService.addRepresentativeContact(newContact).subscribe({
        next: (result) => {
          newContact.id = result.id;
          this.license.representativeContacts.push(newContact);
          this.updateRepresentativeContactsTable();
          this.sharedService.toastr.success('Representative contact added successfully');
          this.representativeContactForm.reset();
          this.showRepresentativeContactForm = false;
        },
        error: (e) => {
          console.log('error adding representative contact', e);
          this.sharedService.toastr.error('Error adding representative contact');
        }
      });
    }
  }

  editRepresentativeContact(contact: IRepresentativeContact): void {
    this.editingRepresentativeContact = contact;
    this.showRepresentativeContactForm = true;

    const isPredefinedOption = this.representativeTypeOptions.includes(contact.representativeType || '');

    this.representativeContactForm.patchValue({
      representativeType: isPredefinedOption ? contact.representativeType : 'Other',
      representativeTypeOther: isPredefinedOption ? '' : contact.representativeType,
      representativeStaff: contact.representativeStaff,
      contactName: contact.contactName,
      contactPhone: contact.contactPhone,
      contactEmail: contact.contactEmail
    });
  }

  deleteRepresentativeContact(contact: IRepresentativeContact): void {
    this.sharedService.openConfirm('Are you sure you want to delete this representative contact?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.licenseService.deleteRepresentativeContact(contact.id).subscribe({
          next: () => {
            this.license.representativeContacts = this.license.representativeContacts.filter(c => c.id !== contact.id);
            this.updateRepresentativeContactsTable();
            this.sharedService.toastr.success('Representative contact deleted successfully');
          },
          error: (e) => {
            console.log('error deleting representative contact', e);
            this.sharedService.toastr.error('Error deleting representative contact');
          }
        });
      }
    });
  }

  cancelEditRepresentativeContact(): void {
    this.editingRepresentativeContact = null;
    this.showRepresentativeContactForm = false;
    this.representativeContactForm.reset();
  }

}
